'use client';

/**
 * CredentialFieldsControl — the `credentials` form control (see `credential-values.ts`).
 *
 * Renders an auth-method picker (written to the sibling form field the spec names) and then one
 * input per field `CREDENTIAL_FIELD_SPEC` declares for that method: `secret` fields are masked
 * password inputs that browsers are told not to autofill or save, `text` fields are plain, `list`
 * fields (OAuth scopes) are chips — see CredentialFieldInput. Its
 * own value is the `{ fieldName: value }` record the store routes take as `credentials`.
 *
 * It starts empty and only ever sends values out — a stored secret is never read back into it.
 */
import { CONNECTABLE_AUTH_METHODS, AUTH_METHOD_LABELS, AUTH_METHOD_HELP } from '@inferrex/inference-schema';
import { CredentialFieldInput } from './CredentialFieldInput';
import { Label } from '../primitives/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../primitives/select';
import { credentialInputsFor } from './credential-values';

export interface CredentialFieldsControlProps {
  /** Field name — used to namespace input ids. */
  readonly name: string;
  /** The current credential record. */
  readonly value: unknown;
  readonly onChange: (value: Record<string, string>) => void;
  /** The auth method currently held in the sibling field. */
  readonly method: unknown;
  /** Write the sibling auth-method field. Absent → the picker is read-only (method fixed elsewhere). */
  readonly onMethodChange?: ((method: string) => void) | undefined;
}

/** Methods that have something to store (`none` takes no credential). */
const STORABLE_METHODS = CONNECTABLE_AUTH_METHODS.filter((m) => credentialInputsFor(m).length > 0);

export function CredentialFieldsControl({ name, value, onChange, method, onMethodChange }: CredentialFieldsControlProps) {
  const current = typeof method === 'string' ? method : '';
  const record = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const fields = credentialInputsFor(current);
  const set = (field: string, v: string) => {
    const next: Record<string, string> = {};
    for (const [k, existing] of Object.entries(record)) if (typeof existing === 'string') next[k] = existing;
    next[field] = v;
    onChange(next);
  };

  return (
    <div className="space-y-(--space-4)">
      <div className="space-y-2">
        <Label htmlFor={`${name}-method`}>Authentication</Label>
        <Select
          value={current}
          onValueChange={(m: string) => {
            // A switch of method starts from a clean record: a secret typed for one method must
            // never ride along, under its old key, into another method's credential.
            onChange({});
            onMethodChange?.(m);
          }}
          disabled={!onMethodChange}
        >
          <SelectTrigger id={`${name}-method`}>
            <SelectValue placeholder="Choose how this system authenticates…" />
          </SelectTrigger>
          <SelectContent>
            {STORABLE_METHODS.map((m) => (
              <SelectItem key={m} value={m}>{AUTH_METHOD_LABELS[m] ?? m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {current && AUTH_METHOD_HELP[current] && (
          <p className="text-xs text-(--text-tertiary)">{AUTH_METHOD_HELP[current]}</p>
        )}
      </div>

      {fields.map((field) => {
        const id = `${name}-${field.name}`;
        const v = record[field.name];
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={id}>
              {field.label}
              {field.required
                ? <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>
                : <span className="ml-1 text-(--text-tertiary)">(optional)</span>}
            </Label>
            <CredentialFieldInput id={id} field={field} value={typeof v === 'string' ? v : ''} onChange={(next) => set(field.name, next)} />
            {field.hint && <p className="text-xs text-(--text-tertiary)">{field.hint}</p>}
          </div>
        );
      })}
    </div>
  );
}
